import Head from 'next/head';
import Script from 'next/script';
import { ReactNode } from 'react';
import { Maybe, Page } from '@/types/generated';
import { NextPageHeadSchema } from '@shared-components/components/NextPage/NextPageHeadSchema';
import { useAppContext } from '@shared-components/context/AppContextProvider.ctx';
export default function NextPageHead({
  title,
  indexed,
  description,
  canonical,
  breadcrumbs,
  breadcrumbTitle,
  path,
  originUrl,
  ogImage,
  contentsCollection
}: Partial<Page> & {
  breadcrumbs?: Maybe<Page>[];
  originUrl: string;
  ogImage: string;
}) {
  const {
    config
  } = useAppContext();
  const {
    salesForceEvergageScript
  } = config.featureFlags;
  const {
    schemaUrl
  } = config;
  let formattedCanonical = canonical;
  if (canonical && !canonical.endsWith('/')) {
    formattedCanonical = `${canonical}/`;
  }
  const ogUrl = formattedCanonical ?? schemaUrl;
  let formattedPath: string | undefined;
  if (path) {
    formattedPath = path.endsWith('/') ? path : `${path}/`;
  }
  const canonicalHref = formattedCanonical ?? (formattedPath ? `${originUrl}${formattedPath}` : undefined);
  let salesforceScript: ReactNode = null;
  if (salesForceEvergageScript === 'Async') {
    salesforceScript = <Script id="salesforce-evergage-async" strategy="afterInteractive" type="text/javascript" async src="//cdn.evgnet.com/beacon/aspendentalmgmtinc2/engage/scripts/evergage.min.js" />;
  } else if (salesForceEvergageScript === 'Sync') {
    salesforceScript = <Script id="salesforce-evergage-sync" strategy="afterInteractive" type="text/javascript" src="//cdn.evgnet.com/beacon/aspendentalmgmtinc2/engage/scripts/evergage.min.js" />;
  }
  return <>
			<Head data-sentry-element="Head" data-sentry-source-file="NextPageHead.tsx">
				<title>{title}</title>
				{!indexed && <meta key="robots" name="robots" content="noindex,nofollow" />}
				{!indexed && <meta key="googlebot" name="googlebot" content="noindex,nofollow" />}
				{description && <meta name="description" content={description} />}
				<link rel="canonical" href={canonicalHref} />
				{/* OG Tags */}
				<meta property="og:image" content={ogImage} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta property="og:image:secure_url" content={ogImage} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta property="og:title" content={title ?? ''} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta property="og:description" content={description ?? ''} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta property="og:url" content={ogUrl} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta name="twitter:image" content={ogImage} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta name="twitter:title" content={title ?? ''} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta name="twitter:description" content={description ?? ''} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				<meta name="twitter:url" content={canonical ?? schemaUrl} data-sentry-element="meta" data-sentry-source-file="NextPageHead.tsx" />
				{salesforceScript}
			</Head>
			<NextPageHeadSchema breadcrumbs={breadcrumbs} breadcrumbTitle={breadcrumbTitle} originUrl={originUrl} title={title} description={description} contentsCollection={contentsCollection} path={path} data-sentry-element="NextPageHeadSchema" data-sentry-source-file="NextPageHead.tsx" />
		</>;
}